import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ShoppingBag, CheckCircle, Share2, Check } from 'lucide-react';
import { PRODUCTS, CATEGORIES } from '../../data/smartcasesProducts';
import { getCart, addToCart, formatEur } from './cart';

export default function SmartcaseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [imgIdx, setImgIdx] = useState(0);
  const [variantIdx, setVariantIdx] = useState('');
  const [payment, setPayment] = useState('ohne');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [shared, setShared] = useState(false);
  const [cartCount, setCartCount] = useState(getCart().reduce((s, i) => s + i.qty, 0));

  const shareLink = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/smartcases/share/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product?.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch (e) { /* abgebrochen */ }
  };

  useEffect(() => {
    const sync = () => setCartCount(getCart().reduce((s, i) => s + i.qty, 0));
    window.addEventListener('smartcases-cart', sync);
    return () => window.removeEventListener('smartcases-cart', sync);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); setImgIdx(0); setVariantIdx(''); setPayment('ohne'); setQty(1); }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-off gap-4">
        <p className="text-lg">Produkt nicht gefunden.</p>
        <Link to="/smartcases" className="btn btn--purple">Zurück zur Übersicht</Link>
      </div>
    );
  }

  const variant = product.variants && variantIdx !== '' ? product.variants[Number(variantIdx)] : null;
  const needsVariant = !!product.variants;
  const currentPrice = (() => {
    if (needsVariant) {
      if (!variant) return null;
      return product.hasPayment && payment === 'mit' ? variant.pricePayment : variant.price;
    }
    return product.price;
  })();
  const minPrice = needsVariant ? Math.min(...product.variants.map((v) => v.price)) : product.price;

  const handleAdd = () => {
    if (needsVariant && !variant) return;
    addToCart({
      slug: product.slug,
      name: product.name,
      variant: variant ? variant.label : null,
      payment: product.hasPayment ? (payment === 'mit' ? 'mit Payment' : 'ohne Payment') : null,
      price: currentPrice,
      qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="min-h-screen bg-off">
      <div className="sec--deep border-b-2 border-ink">
        <div className="wrap py-4 flex items-center justify-between gap-4">
          <Link to="/smartcases" className="flex items-center gap-2 text-sm font-medium border-b-2 border-transparent hover:border-lilac hover:text-lilac transition-colors" data-testid="back-to-catalog">
            <ArrowLeft className="w-4 h-4" /> SmartCases Übersicht
          </Link>
          <button onClick={() => navigate('/smartcases')} className="btn btn--lilac btn--xs" data-testid="detail-cart-button">
            <ShoppingBag className="w-4 h-4" /> Einkaufszettel {cartCount > 0 && <span className="bg-ink text-off text-xs px-2 py-0.5 font-sans">{cartCount}</span>}
          </button>
        </div>
      </div>

      <div className="wrap py-10 md:py-14 grid md:grid-cols-2 gap-10">
        {/* Bilder-Slider */}
        <div>
          <div className="relative card-ink card-ink--shadow overflow-hidden aspect-square" data-testid="image-slider">
            {product.images[imgIdx] && <img src={product.images[imgIdx]} alt={product.name} className="w-full h-full object-contain p-6" />}
            {product.images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((imgIdx - 1 + product.images.length) % product.images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-off border-2 border-ink p-2 hover:bg-lilac" data-testid="slider-prev"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => setImgIdx((imgIdx + 1) % product.images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-off border-2 border-ink p-2 hover:bg-lilac" data-testid="slider-next"><ChevronRight className="w-5 h-5" /></button>
              </>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 mt-5 overflow-x-auto pb-1">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-16 h-16 flex-shrink-0 bg-white border-2 overflow-hidden ${i === imgIdx ? 'border-purple shadow-ink-xs' : 'border-ink/30'}`} data-testid={`thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Infos & Konfiguration */}
        <div>
          <p className="label-caps text-purple mb-2">{CATEGORIES[product.category]}</p>
          <h1 className="display-2" data-testid="detail-name">{product.name}</h1>
          <p className="font-display font-extrabold text-2xl md:text-3xl mt-4" data-testid="detail-price">
            {currentPrice !== null ? formatEur(currentPrice) : `ab ${formatEur(minPrice)}`}
          </p>
          <p className="text-xs text-ink/60 mt-1" data-testid="detail-net-note">Netto-Preis zzgl. MwSt.</p>
          <p className="mt-5 leading-relaxed" data-testid="detail-description">{product.description}</p>
          <p className="text-xs text-ink/60 mt-2" data-testid="detail-deviation-note">Optische Abweichungen möglich.</p>

          <div className="mt-8 space-y-5">
            {needsVariant && (
              <div>
                <label className="field-label">{product.variantLabel || 'Gerätemodell'} *</label>
                <select value={variantIdx} onChange={(e) => setVariantIdx(e.target.value)} className="field-input" data-testid="variant-select">
                  <option value="">Bitte auswählen...</option>
                  {product.variants.map((v, i) => (
                    <option key={i} value={i}>
                      {v.label} – {formatEur(product.hasPayment && payment === 'mit' ? v.pricePayment : v.price)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {product.hasPayment && (
              <div>
                <label className="field-label">Kartenzahlung</label>
                <div className="grid grid-cols-2 gap-2" data-testid="payment-toggle">
                  {[['ohne', 'Ohne Payment'], ['mit', 'Mit Payment']].map(([val, label]) => (
                    <button key={val} onClick={() => setPayment(val)} className={`border-2 border-ink px-3 py-2.5 text-sm font-display font-bold transition-colors ${payment === val ? 'bg-purple text-off shadow-ink-xs' : 'bg-off hover:bg-lilac'}`} data-testid={`payment-${val}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-ink/70 mt-2">„Mit Payment": inklusive Kartenzahlungs-Aufnahme und Adapter – bereit für deinen Kartenleser.</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="field-label mb-0">Menge</label>
              <div className="flex items-center border-2 border-ink bg-off">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 font-bold hover:bg-lilac" data-testid="qty-minus">−</button>
                <span className="w-10 text-center text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-3 py-2 font-bold hover:bg-lilac" data-testid="qty-plus">+</button>
              </div>
            </div>

            <button onClick={handleAdd} disabled={needsVariant && !variant} className={`btn btn--block ${added ? 'btn--wa' : 'btn--purple'}`} data-testid="add-to-cart-button">
              {added ? (<><CheckCircle className="w-5 h-5" /> Auf dem Einkaufszettel!</>) : (<><ShoppingBag className="w-5 h-5" /> Auf den Einkaufszettel</>)}
            </button>
            {needsVariant && !variant && <p className="text-xs text-ink/60">Bitte wähle zuerst {product.variantLabel === 'Gerätemodell' ? 'dein Gerätemodell' : `eine Option (${product.variantLabel})`} aus.</p>}
            {added && <Link to="/smartcases" className="block text-center text-sm text-purple font-bold underline underline-offset-4 hover:text-deep" data-testid="go-to-cart-link">Zum Einkaufszettel & Angebot anfordern →</Link>}

            <button onClick={shareLink} className="btn btn--ghost btn--sm btn--block" data-testid="share-product-button">
              {shared ? (<><Check className="w-4 h-4" /> Link kopiert!</>) : (<><Share2 className="w-4 h-4" /> Produkt-Link teilen</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
